import React, { useCallback, useEffect, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Dropdown, DropdownButton, Form, Modal } from "react-bootstrap";
import { getTableData } from "./ReportsAPI";
import RepliesModal from "./RepliesModal";
import JsonCollapse from "../../Builder/Components/JsonCollapse";
import { proxyAPI } from "../../proxyAPI";
import MyPagination from "../../Common/MyPagination";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import NoDataPlaceholder from "../../Common/NoDataPlaceholder";
import EventsTimeLine from "../../Events/EventsTimeLine";

function InfoFormatter({ row }) {
  const [info, setInfo] = useState({});
  const [show, setShow] = useState(false);
  useEffect(() => {
    (async () => {
      if (!show) return;

      // message

      if (row.message) {
        setInfo((p) => ({
          ...p,
          message: row.message,
        }));
      }

      // broadcaster data
      const broadcasterData = row.metadata.find(
        (md) => md.kind === "broadcaster"
      )?.data;
      if (broadcasterData) {
        const broadcasterUUID = broadcasterData.find(
          (md) => md.key === "broadcaster_template"
        )?.value;
        if (broadcasterUUID) {
          const broadcaster = await proxyAPI({
            prefix: "logic/workflows/api",
            url: `broadcasters/${broadcasterUUID}`,
          });
          setInfo((p) => ({ ...p, broadcaster }));
        }
        const sentBy = broadcasterData.find(
          (md) => md.key === "sent_by"
        )?.value;
        const event = broadcasterData.find((md) => md.key === "event");

        setInfo((p) => ({ ...p, sentBy, event }));
      }
      // workflow data
      const workflowData = row.metadata.find(
        (md) => md.kind === "workflow"
      )?.data;
      if (workflowData) {
        const project = workflowData.find((md) => md.key === "project");
        const workflow = workflowData.find((md) => md.key === "workflow");
        const process = workflowData.find((md) => md.key === "process");
        setInfo((p) => ({ ...p, project, workflow, process }));
      }
      // inbound data
      const inboundData = row.metadata.find(
        (md) => md.kind === "handler:inbound"
      )?.data;
      if (inboundData) {
        const inboundAction = inboundData.find((md) => md.key === "action");
        const inboundHandler = inboundData.find((md) => md.key === "handler");
        const inboundHandlerEvent = inboundData.find(
          (md) => md.key === "handler_event"
        );
        setInfo((p) => ({
          ...p,
          inboundAction,
          inboundHandler,
          inboundHandlerEvent,
        }));
      }
    })();
  }, [show]);

  return (
    <>
      <button type="button" className="btn p-0" onClick={() => setShow(true)}>
        <FontAwesomeIcon icon={faInfoCircle} />
      </button>

      <Modal
        show={show}
        size="lg"
        onHide={() => setShow(false)}
        backdropClassName="z-2999"
        className="z-3000"
      >
        <Modal.Header closeButton>
          <Modal.Title>Message Info</Modal.Title>
        </Modal.Header>
        <div className="p-3">
          <div className="row">
            {info.broadcaster && (
              <>
                <div className="col-3"> Label</div>
                <div className="col-9">{info.broadcaster?.Label}</div>
                <div className="col-3">Description</div>
                <div className="col-9">{info.broadcaster?.Description}</div>
              </>
            )}
            {info.sentBy && (
              <>
                <div className="col-3">Sent by</div>
                <div className="col-9">{info.sentBy}</div>
              </>
            )}
            {info.event && (
              <>
                <div className="col-3">Event</div>
                <div className="col-9">
                  <EventsTimeLine
                    eventUuid={info.event.value}
                    label={info.event.label}
                    hideAllMessages
                  />
                </div>
              </>
            )}
            {info.project && (
              <>
                <div className="col-3">Project label</div>
                <div className="col-9">{info.project.label}</div>
              </>
            )}
            {info.workflow && (
              <>
                <div className="col-3">Workflow label</div>
                <div className="col-9">{info.workflow.label}</div>
              </>
            )}
            {info.process && (
              <>
                <div className="col-3">Process</div>
                <div className="col-9">{info.process.value}</div>
              </>
            )}
            {info.inboundHandler && (
              <>
                <div className="col-3">Inbound Handler</div>
                <div className="col-9">{info.inboundHandler.label}</div>
              </>
            )}
            {info.inboundAction && (
              <>
                <div className="col-3">Inbound handler action</div>
                <div className="col-9">{info.inboundAction.value}</div>
              </>
            )}
            {info.inboundHandlerEvent && (
              <>
                <div className="col-3">Inbound handler event</div>
                <div className="col-9">{info.inboundHandlerEvent.value}</div>
              </>
            )}
          </div>

          <JsonCollapse json={row.metadata} />
        </div>
      </Modal>
    </>
  );
}

function Table({ filters, loading, setLoading }) {
  // const [sp] = useSearchParams();
  // const { projectUUID, broadcastUUID, inboundUUID } = Object.fromEntries(
  //   sp.entries()
  // );
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(12);
  const [showReplies, setShowReplies] = useState(null);
  // const [needsRefresh, setNeedsRefresh] = useState(false);

  const handlePaginate = useCallback((newPage, newPerPage) => {
    setPage(newPage);
    setPerPage(newPerPage);
  }, []);

  // try again for failed due to no credit
  const tryAgainHandler = (transId) => {
    alert(`tried ${transId}`);
  };

  // bootstrap-table change handler
  const tableChangeHandler = (type, { page: newPage, sizePerPage }) => {
    setPage(newPage);
    setPerPage(sizePerPage);
  };

  const statusFormatter = (cell, row) => (
    <>
      {row.status_group.charAt(0).toUpperCase() + row.status_group.slice(1)} (
      {cell})
      {
        // Show try again button if failed because of no credit
        cell === "XYZ" && ( // hide feature for now
          <button
            title="Try again"
            type="button"
            className="btn btn-xs btn-link"
            onClick={() => tryAgainHandler(row.trans_id)}
            aria-label="Try again"
          >
            <i className="fa fa-refresh" />
          </button>
        )
      }
    </>
  );

  const totalRepliesFormatter = (cell, row) => {
    let text;
    // if the message has replies then show number of replies in brackets
    if (
      row.comms_channel === "email" ||
      (row.comms_channel === "sms" && row.trans_type === "threaded") ||
      cell > 0
    ) {
      text = `(${cell} replies)`;
    }

    return (
      <button
        onClick={() =>
          setShowReplies({
            id: row.trans_id,
            message: row.message.message,
            channel: row.comms_channel,
          })
        }
        type="button"
        className="btn btn-xs p-0 px-2 btn-secondary"
        title="Show replies"
      >
        show message {text}
      </button>
    );
  };

  const repliesUntilFormatter = (cell, row) => {
    // only emails and threaded smss accept replies
    if (
      row.comms_channel === "email" ||
      (row.comms_channel === "sms" && row.trans_type === "threaded")
    ) {
      return `${new Date(`${cell.split(" ").join("T")}Z`).toLocaleString()} (${
        row.closed === "Y" ? "closed ticket" : "open ticket"
      })`;
    }
    return "-";
  };

  const columns = [
    {
      dataField: "trans_id",
      text: "ID",
      formatter: (cell) => (
        <span> {`#${cell.toString().padStart(8, "0")}`}</span>
      ),
    },
    {
      dataField: "",
      text: "App",
      formatter: (cell, row) => (
        <span className="text-nowrap">
          {row.metadata?.[0]?.kind || "other"}{" "}
          <InfoFormatter cell={cell} row={row} />
        </span>
      ),
    },
    { dataField: "comms_channel", text: "Type" },
    {
      dataField: "sent_datetime",
      text: "Received @",
      formatter: (cell) =>
        new Date(`${cell.split(" ").join("T")}Z`).toLocaleString(),
    },
    {
      dataField: "processed_at",
      text: "Processed @",
      formatter: (cell) =>
        cell
          ? new Date(`${cell.split(" ").join("T")}Z`).toLocaleString()
          : "N/A",
    },
    {
      dataField: "validity",
      text: "Replies Until",
      formatter: repliesUntilFormatter,
    },
    {
      dataField: "status",
      text: "Status",
      formatter: statusFormatter,
    },
    { dataField: "origin", text: "Origin" },
    { dataField: "destination", text: "Destination" },
    {
      dataField: "closed",
      text: "Closed",
      formatter: (cell) => (cell === "Y" ? "Yes" : "No"),
    },
    {
      dataField: "billable",
      text: "Billable Records",
    },
    {
      dataField: "total_replies",
      text: "Message/Replies",
      formatter: totalRepliesFormatter,
    },
    { dataField: "account_name", text: "Account" },
  ];

  const [selectedColumns, setSelectedColumns] = useState(
    JSON.parse(localStorage.getItem("selectedTransactionsColumns")) ??
      columns.map((c) => c.dataField)
  );

  const getData = (controller) => {
    // use abort controller to signal cancellation
    // https://dev.to/chandelieraxel/why-you-should-cancel-your-api-calls-in-react-410l
    setLoading(true);
    getTableData(
      {
        ...filters,
        // ...(projectUUID ? { projectUUID } : {}),
        // ...(broadcastUUID ? { broadcastUUID } : {}),
        // ...(inboundUUID ? { inboundUUID } : {}),
        offset: page * perPage,
        limit: perPage,
      },
      controller
    ).then((newData) => {
      setData(newData);
      setLoading(false);
    });
  };

  // get new data everytime filters, page or perPage changes
  // page and perPage is set by tableChangeHandler during pagination
  useEffect(() => {
    if (filters) {
      const controller = new AbortController();
      getData(controller);
      // return () => controller.abort();
    }
  }, [filters, page, perPage]);

  return (
    <>
      <div>
        <div className="panel-heading widget-header d-flex justify-content-between">
          <h3 className="panel-title padding-10">Transactions</h3>
          <DropdownButton
            id="show-colums-dropdown"
            title="Show columns"
            variant="colored"
            autoClose="outside"
            align="end"
            className="h-100 rounded-3"
          >
            {columns.map((c) => (
              <Dropdown.Item as="div">
                <Form.Check
                  checked={selectedColumns.includes(c.dataField)}
                  label={c.text}
                  id={`${c.dataField}-checkbox`}
                  onChange={() => {
                    setSelectedColumns((p) => {
                      let newColumns;
                      if (p.includes(c.dataField)) {
                        newColumns = p.filter((pc) => pc !== c.dataField);
                      } else {
                        newColumns = [...p, c.dataField];
                      }
                      localStorage.setItem(
                        "selectedTransactionsColumns",
                        JSON.stringify(newColumns)
                      );
                      return newColumns;
                    });
                  }}
                />
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </div>
        <div className="panel-body">
            <div className="table-responsive">
              <BootstrapTable
                key={selectedColumns?.join("-")}
                remote
                keyField="trans_id"
                data={data?.reports ?? []}
                columns={columns.filter((c) =>
                  selectedColumns.includes(c.dataField)
                )}
                bordered={false}
                hover
                // pagination={paginationFactory({
                //   page,
                //   sizePerPage: perPage,
                //   totalSize: data?.total ?? 0,
                //   hidePageListOnlyOnePage: true,
                //   sizePerPageList: [
                //     {
                //       text: "10",
                //       value: 10,
                //     },
                //     {
                //       text: "50",
                //       value: 50,
                //     },
                //   ],
                // })}
                onTableChange={tableChangeHandler}
              />
            </div>

            {!data?.total && <NoDataPlaceholder />}
            <MyPagination
              total={data?.total ?? 0}
              onPaginate={handlePaginate}
            />
            {data?.error && (
              <div className="text-center red-800">{data?.error}</div>
            )}
        </div>
      </div>
      <RepliesModal showReplies={showReplies} setShowReplies={setShowReplies} />
    </>
  );
}

export default Table;
